import { ChangeDetectionStrategy, Component, computed, forwardRef, input, model } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ngClassDirective } from '@frontendlib/common/directives/ngClass/ngClass.directive';
import { Functions } from '@sharedlib/common/lang/Functions';
import { Implements } from '@sharedlib/common/lang/Implements';

export type Toggle2Size = 's' | 'm' | 'l' | 'xl';
export type Toggle2Appearance = 'primary' | 'secondary';

@Component({
  standalone: true,
  selector: 'app-toggle-switch2',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './toggle-switch2.component.html',
  imports: [ngClassDirective],
  styles: [
    `
      :host {
        display: inline-flex;
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => ToggleSwitch2Component),
    },
  ],
})
export class ToggleSwitch2Component implements ControlValueAccessor {
  public readonly appearance = input<Toggle2Appearance>('primary');
  public readonly size = input<Toggle2Size>('m');
  public readonly disabled = model<boolean>(false);

  public readonly checkedLabel = input<string>();
  public readonly uncheckedLabel = input<string>();

  public readonly checked = model<boolean>(false);

  protected readonly circleSize = computed(() => {
    switch (this.size()) {
      case 's':
        return 16;
      case 'm':
        return 24;
      case 'l':
        return 32;
      case 'xl':
        return 40;
      default:
        return 24;
    }
  });
  protected readonly gapSize = computed(() => {
    switch (this.size()) {
      case 's':
        return 4;
      case 'm':
        return 4;
      case 'l':
        return 6;
      case 'xl':
        return 6;
      default:
        return 4;
    }
  });

  protected onToggle(event: Event): void {
    if (this.disabled()) {
      return;
    }
    const target = event.target as HTMLInputElement;
    this.checked.set(target.checked);
    this.onTouched?.();
    this.onChanged?.(target.checked);
  }

  private onChanged?: Functions.Consumer<boolean>;
  private onTouched?: Functions.Callback;

  @(Implements<ControlValueAccessor>)
  public writeValue(checked: boolean): void {
    this.checked.set(checked);
  }

  @(Implements<ControlValueAccessor>)
  public registerOnChange(fn: Functions.Consumer<boolean>): void {
    this.onChanged = fn;
  }

  @(Implements<ControlValueAccessor>)
  public registerOnTouched(fn: Functions.Callback): void {
    this.onTouched = fn;
  }

  @(Implements<ControlValueAccessor>)
  public setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
